import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { INVOCATION_TYPES, type InvocationTypeEntry } from '../../../lib/mcpAnalytics/invocationTypes'
import {
  formatBucketFullLabel,
  formatBucketLabel,
  formatMonthMarker,
  isFirstDayOfMonth,
  type BucketGranularity,
} from '../../../lib/formatDateTime'

/* ─── Invocations-over-time: stacked column chart ───

   CSS/flex, no charting dependency: the prototype's chart is CSS
   (agent-studio.html:5327-5368) and porting it keeps MS-701 AC3a's "one shared
   token pair for axis weight and colour" enforceable, without a new package
   through Snyk for four stacked bars.

   The x-axis is **continuous**: one column per entry in `buckets`, which the API
   returns as a complete spine over the requested window. A bucket with no matching
   point renders as a gap, so the axis reads as real time rather than as "the days
   that happened to have activity". The client never enumerates the buckets itself
   — a browser walking `new Date()` midnights walks *local* midnights, which do not
   line up with the server's keys for any viewer off UTC. */

export interface SeriesPoint {
  bucket: string
  invocationType: string
  invocations: number
}

export interface StackedTimeSeriesProps {
  buckets: string[]
  points: SeriesPoint[]
  granularity: BucketGranularity
  /** The viewer's IANA zone — the same one the buckets were truncated in. */
  timeZone?: string
  /** Non-null isolates one type; the others are hidden rather than dimmed. */
  isolatedTypeKey: string | null
  selectedBucket: string | null
  onSelectBucket: (bucket: string | null) => void
  emptyLabel: string
}

const CHART_HEIGHT_PX = 218 // plot 248 − 30px x-axis row
const GRIDLINE_COUNT = 5
const X_AXIS_ROW_PX = 30

/** Round a maximum up to a value that divides into five readable gridlines. */
export function niceMax(value: number): number {
  if (value <= GRIDLINE_COUNT) return GRIDLINE_COUNT
  const magnitude = 10 ** Math.floor(Math.log10(value))
  const normalised = value / magnitude
  const step = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10
  return step * magnitude
}

/** `{ [bucket]: { [typeKey]: count } }`, folded onto the four canonical types so
 *  `initialize` lands in the same column as `ping`. */
export function foldPointsByBucket(
  points: SeriesPoint[],
  types: readonly InvocationTypeEntry[],
): Record<string, Record<string, number>> {
  const keyForStored = new Map<string, string>(
    types.flatMap(t => t.storedValues.map(v => [v, t.key] as const)),
  )
  const folded: Record<string, Record<string, number>> = {}
  for (const point of points) {
    const typeKey = keyForStored.get(point.invocationType)
    if (!typeKey) continue // an unrecognised type is a data gap, not a silent bucket
    folded[point.bucket] ??= {}
    folded[point.bucket][typeKey] = (folded[point.bucket][typeKey] ?? 0) + point.invocations
  }
  return folded
}

export function StackedTimeSeries({
  buckets,
  points,
  granularity,
  timeZone,
  isolatedTypeKey,
  selectedBucket,
  onSelectBucket,
  emptyLabel,
}: StackedTimeSeriesProps) {
  const { t, i18n } = useTranslation()
  const [hovered, setHovered] = useState<{ bucket: string; x: number; y: number } | null>(null)

  const visibleTypes = isolatedTypeKey
    ? INVOCATION_TYPES.filter(type => type.key === isolatedTypeKey)
    : INVOCATION_TYPES
  const counts = foldPointsByBucket(points, INVOCATION_TYPES)
  const totalFor = (bucket: string) =>
    visibleTypes.reduce((sum, type) => sum + (counts[bucket]?.[type.key] ?? 0), 0)

  const grandTotal = buckets.reduce((sum, bucket) => sum + totalFor(bucket), 0)
  if (buckets.length === 0 || grandTotal === 0) {
    return (
      <div className="ts-plot">
        <div className="ts-empty">{emptyLabel}</div>
      </div>
    )
  }

  const yMax = niceMax(Math.max(...buckets.map(totalFor), 1))
  const step = yMax / GRIDLINE_COUNT
  const isDay = granularity === 'day'
  const options = { timeZone, locale: i18n.language }

  return (
    <>
      <div className={isDay ? 'ts-plot gran-day' : 'ts-plot'}>
        <div className="ts-axisy" />
        {Array.from({ length: GRIDLINE_COUNT + 1 }, (_, i) => (
          <div
            key={i}
            className={i === 0 ? 'ts-gridline base' : 'ts-gridline'}
            style={{ bottom: X_AXIS_ROW_PX + (i / GRIDLINE_COUNT) * CHART_HEIGHT_PX }}
          >
            <span className="ts-gridval">{i * step}</span>
          </div>
        ))}

        <div className="ts-chart">
          {buckets.map(bucket => {
            const total = totalFor(bucket)
            return (
              <div
                key={bucket}
                className={selectedBucket === bucket ? 'ts-col sel' : 'ts-col'}
                data-bucket={bucket}
                role="button"
                tabIndex={0}
                aria-pressed={selectedBucket === bucket}
                aria-label={`${formatBucketFullLabel(bucket, granularity, options)}: ${total}`}
                onClick={() => onSelectBucket(selectedBucket === bucket ? null : bucket)}
                onKeyDown={e => {
                  if (e.key !== 'Enter' && e.key !== ' ') return
                  e.preventDefault()
                  onSelectBucket(selectedBucket === bucket ? null : bucket)
                }}
                onMouseMove={e => setHovered({ bucket, x: e.clientX, y: e.clientY })}
                onMouseLeave={() => setHovered(null)}
              >
                <div className="ts-bar" style={{ height: `${(total / yMax) * 100}%` }}>
                  {visibleTypes.map(type => {
                    const value = counts[bucket]?.[type.key] ?? 0
                    if (value <= 0) return null
                    return (
                      <div
                        key={type.key}
                        className="ts-seg"
                        style={{
                          height: `${((value / total) * 100).toFixed(2)}%`,
                          background: type.colorVar,
                        }}
                      />
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>

        <div className="ts-xrow">
          {buckets.map((bucket, index) => {
            if (!isDay) {
              return (
                <div key={bucket} className={selectedBucket === bucket ? 'ts-xcell sel' : 'ts-xcell'}>
                  {formatBucketLabel(bucket, granularity, options)}
                </div>
              )
            }
            // Continuous daily timeline: a marker at the first day of each month
            // (plus the first column), rather than 91 unreadable day numbers.
            const isMonthStart = index === 0 || isFirstDayOfMonth(bucket, options)
            return (
              <div
                key={bucket}
                className={[
                  'ts-xcell mstart',
                  isMonthStart ? 'show' : '',
                  selectedBucket === bucket ? 'sel' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {isMonthStart ? formatMonthMarker(bucket, options) : ''}
              </div>
            )
          })}
        </div>
      </div>

      {hovered && (
        <div
          className="ts-tip open"
          role="tooltip"
          style={{ left: Math.min(hovered.x + 14, window.innerWidth - 190), top: Math.max(hovered.y - 120, 8) }}
        >
          <div className="ts-tip-date">
            {formatBucketFullLabel(hovered.bucket, granularity, options)}
          </div>
          {visibleTypes.map(type => (
            <div key={type.key} className="ts-tip-row">
              <span className="ts-tip-sw" style={{ background: type.colorVar }} />
              {type.label}
              <span className="ts-tip-val">{counts[hovered.bucket]?.[type.key] ?? 0}</span>
            </div>
          ))}
          <div className="ts-tip-total">
            {t('mcpAnalytics.detail.invocations.tooltipTotal')}
            <span className="ts-tip-val">{totalFor(hovered.bucket)}</span>
          </div>
        </div>
      )}
    </>
  )
}
